export interface OneFFSubmitOrderPayloadShippingContainer {
  container_id: string;
  delivery_id: string;
  delivery_slot_id: string;
  fulfillment_contract_data?: { name: string; value: string }[];
  complementary_data?: { name: string; value: string }[];
}

export interface OneFFSubmitOrderPayloadShipping {
  shipping_id: string;
  offer_id: string;
  containers: OneFFSubmitOrderPayloadShippingContainer[];
}

export interface OneFFSubmitOrderPayload {
  order_id: string;
  scenario_id: string;
  shippings: OneFFSubmitOrderPayloadShipping[];
}

export interface OneFFValidateOrderContractDataItem {
  name: string;
  value: string;
}

export interface OneFFValidateOrderContainerItem {
  item_id: string;
  sku_id: string;
  option_id: string;
  seller_id: string;
  total_amount: number;
  quantity: number;
}

export interface ValidateOrderContainer {
  container_id: string;
  items: OneFFValidateOrderContainerItem[];
  delivery_amount: number;
  payment_detail: {
    total_amount: number;
    to_collect_amount: number;
  };
}

export interface OneFFValidateOrderShipping {
  shipping_id: string;
  containers: ValidateOrderContainer[];
}

export interface OneFFValidateOrderPayload {
  order_contract_data?: OneFFValidateOrderContractDataItem[];
  shippings?: OneFFValidateOrderShipping[];
  currency_code?: string;
}

export interface OneFFAvailabilityOptions {
  option_id: string;
  seller_id: string;
  quantity_type: 'LIMITED';
  quantity: number;
  prices: { price_id: string }[];
  start_sec: number;
  duration_sec: number;
  zone_ids: string[];
  zone_store_pickups: unknown[];
}

export interface OneFFAvailability {
  origin_id: string;
  fulfiller_id: string;
  fulfillment_types: string[];
  options: OneFFAvailabilityOptions[];
}

export interface OneFFAvailabilities {
  sku_type: string;
  sku_id: string;
  availabilities: OneFFAvailability[];
  next_availabilities: OneFFAvailability[];
}

export interface OneFFAvailabilitiesPayload {
  items: { sku_id: string }[];
  fulfillment_types?: OneFFFulfillementTypes[];
  location_context: {
    country: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
    radius?: number;
    postal_code?: string;
    zone_ids?: string[];
  };
}

export interface OneFFAvailabilityOptionPayload {
  items: {
    sku_id: string;
    fulfiller_id: string;
    option_id: string;
  }[];
  location_context: {
    country: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
    radius?: number;
    postal_code?: string;
    zone_ids?: string[];
  };
}

export interface OneFFShippingItemPrice {
  price_id: string;
  unit_price: number;
}

export interface OneFFDeliveryGroupItem {
  sku_id: string;
  option_id: string;
  price: OneFFShippingItemPrice;
  fulfiller_id: string;
  quantity: number;
  sku_contract_data?: { name: string; value: string }[];
}

export enum OneFFFulfillementTypes {
  BOOKING = 'BOOKING',
}

export interface OneFFScenarioDeliveryGroup {
  items: OneFFDeliveryGroupItem[];
  fulfillment_types: OneFFFulfillementTypes[];
  location_context: OneFFLocationContext;
}

export interface OneFFLocationContext {
  country: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  radius?: number;
  postal_code?: string;
  zone_ids: string[];
}

export interface OneFFScenarioPayload {
  delivery_groups: OneFFScenarioDeliveryGroup[];
  currency_code: string;
}

export interface OneFFShippingItem {
  item_id: string;
  sku_id: string;
  option_id: string;
  price: OneFFShippingItemPrice;
  seller_id: string;
  quantity: number;
  sku_contract_data?: { name: string; value: string }[];
}

export interface OneFFDeliverySlot {
  delivery_slot_id: string;
  date_from: string;
  date_to: string;
  time_window: {
    time_from: string;
    time_to: string;
  };
  cost: {
    amount: number;
    currency_code: string;
    tax_rate: number;
  };
}

export interface OneFFDelivery {
  delivery_id: string;
  fulfillment_type: OneFFFulfillementTypes;
  carbon_footprint: number;
  delivery_slots: OneFFDeliverySlot[];
}

export interface OneFFContainer {
  container_id: string;
  items: { item_id: string; quantity: number }[];
  deliveries: OneFFDelivery[];
  cash_on_delivery_allowed: boolean;
  tags: string[];
}

export enum OneFFOfferStatus {
  COMPLETE = 'COMPLETE',
}

export interface OneFFOffer {
  offer_id: string;
  containers: OneFFContainer[];
  status: OneFFOfferStatus;
}

export enum OneFFShippingStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export interface OneFFShipping {
  shipping_id: string;
  fulfiller_id: string;
  items: OneFFShippingItem[];
  offers: OneFFOffer[];
  status: OneFFShippingStatus;
}

export interface OneFFScenario {
  scenario_id: string;
  currency_code: string;
  shippings: OneFFShipping[];
  expiry_sec: number;
}

export interface OneFFSubmitShippingDetails {
  shipping_id: string;
  submit_status: string;
  failure_reason?: string;
}

export interface OneFFSubmitOrder {
  confirmed?: boolean;
  submitted: boolean;
  shippings: OneFFSubmitShippingDetails[];
  expiry_sec: number;
}

export interface OneFFValidateOrder {}

export interface OneFFErrorResponse {
  timestamp: number;
  status: number;
  messages?: string[];
  message?: string;
  detail: string;
  path: string;
}

export const isOneFFErrorResponse = <T>(
  response: T | OneFFErrorResponse,
): response is OneFFErrorResponse => {
  return (
    (response as OneFFErrorResponse)?.timestamp !== undefined &&
    (response as OneFFErrorResponse)?.status !== undefined
  );
};

export interface OneFFAvailabilitiesByServicePayload {
  fulfiller_id: string;
  sku_id: string;
  option_id: string;
  location_context: {
    country: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
    radius?: number;
    postal_code?: string;
    zone_ids?: string[];
  };
  search_period: {
    start_sec: number;
    end_sec?: number;
  };
  max_result?: number;
  resource_ids_filter?: string[];
}

export interface OneFFAvailabilitiesByServiceItemOptionTimeSlot {
  zone_ids: string[];
  start_sec: number;
  duration_sec: number;
  quantity: number;
}

export interface OneFFAvailabilitiesByServiceItemOption {
  option_id: string;
  discontinued: boolean;
  has_more_results_within_search_period: boolean;
  time_slots: OneFFAvailabilitiesByServiceItemOptionTimeSlot[];
}

export interface OneFFAvailabilitiesByServiceItem {
  fulfiller_id: string;
  options: OneFFAvailabilitiesByServiceItemOption[];
}

export interface OneFFAvailabilitiesByServiceResponse {
  sku_id: string;
  prices: {
    price_id: string;
    countries: string[];
  }[];
  availabilities: OneFFAvailabilitiesByServiceItem[];
}

export interface OneFFStoreServiceAvailabilityCoordinates {
  lat: number;
  lng: number;
}
export interface OneFFStoreServiceAvailabilityPayload {
  sku_id: string;
  fulfiller_id: string;
  option_id: string;
  location_context: {
    coordinates: OneFFStoreServiceAvailabilityCoordinates;
    radius: number;
  };
  search_period: {
    start_sec: number;
    end_sec: number;
  };
  max_result?: number;
}
export interface OneFFStoreServiceAvailability {
  pickup_point_id: string;
  pickup_point_provider_id: string;
  name: string;
  zone_id: string;
  external_pickup_point_id: string;
  pickup_point_addresses: any[];
  pickup_point_phone_number: string;
  country_code: string;
  pickup_point_definition: {
    type: string;
    coordinates: number[];
  };
  distance: number;
  next_available_time_slots: {
    start_sec: number;
    duration_sec: number;
    quantity: number;
  }[];
  service_active: boolean;
}
